<template>
  <div class="autocomplete">
    <div class="autocomplete__wrapper w-full">
      <UiIcon v-if="!loading" class="autocomplete__icon" icon="search" />
      <UiSpinnerCircle v-else class="autocomplete__loader" />
      <span
        v-if="!valueLocal"
        class="autocomplete__placeholder pointer-events-none"
      >
        {{ placeholder }}
      </span>
      <AutoComplete
        ref="autocomplete"
        v-model="valueLocal"
        class="w-full"
        :class="[
          {
            'pointer-events-none cursor-not-allowed opacity-60':
              disabled || loading,
            disabled: disabled || loading,
          },
        ]"
        v-bind="$attrs"
        :suggestions="suggestions"
        :show-empty-message="false"
        @click="
          () => {
            $emit('click');
            // autocomplete ? autocomplete.showOverlay() : null;
          }
        "
        @complete="$emit('complete', $event)"
        @item-select="$emit('item-select', $event)"
        @focus="
          () => {
            $emit('focus');
          }
        "
        @blur="
          () => {
            $emit('blur');
          }
        "
      >
        <template #option="slotProps">
          <slot name="acItem" :item="slotProps.option" />
        </template>
      </AutoComplete>
    </div>
    <div
      v-if="!loading && suggestions && suggestions.length"
      class="autocomplete__count mt-4"
    >
      {{ $t('found') }} <span>{{ suggestions.length }}</span>
      {{ countType }}
    </div>
    <div
      v-else-if="
        !loading &&
        suggestions !== null &&
        (!suggestions || !suggestions.length)
      "
      class="autocomplete__count mt-4"
    >
      {{ $t('nothing_found') }}
    </div>
    <span v-if="errors.length" class="block py-1 text-red-lighten1">
      {{ errors[0] }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import AutoComplete from 'primevue/autocomplete';

interface Props {
  id?: string | null;
  name?: string | null;
  placeholder?: string | null;
  modelValue?: string | null;
  errors?: any[];
  block?: boolean;
  disabled?: boolean;
  loading?: boolean;
  countType?: string;
  suggestions?: any[] | null;
}

interface Emits {
  (e: 'update:modelValue', data: Props['modelValue']): void;
  (e: 'focus' | 'blur' | 'click'): void;
  (e: 'complete' | 'item-select', data: any): void;
}

const props = withDefaults(defineProps<Props>(), {
  id: null,
  name: null,
  placeholder: null,
  modelValue: null,
  errors: () => [],
  block: false,
  disabled: false,
  loading: false,
  countType: '',
  suggestions: null,
});

const emit = defineEmits<Emits>();

const valueLocal = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emit('update:modelValue', newValue);
  },
});
const autocomplete = ref();
</script>
<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<style scoped>
.autocomplete__wrapper {
  position: relative;
}

.autocomplete__icon {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 16px;
  width: 24px;
  height: 24px;
  transform: translateY(-50%);
  color: #818d96;
}
.autocomplete__loader {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 16px;
  width: 24px;
  height: 24px;
  transform: translateY(-50%);
  padding: 0;
}
.autocomplete__loader :deep(svg) {
  margin: 0;
  width: 100%;
  height: 100%;
}
.autocomplete__placeholder {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 0;
  width: 100%;
  transform: translateY(-50%);
  color: #818d96;
  padding-left: calc(1.5rem + 24px + 12px);
}
.autocomplete__count {
  color: #818d96;
}
.autocomplete__count span {
  color: var(--prestigio-color-text-primary);
}
.p-autocomplete.disabled :deep(.p-autocomplete-input) {
  background-color: #e1e2e3;
}
:deep(.p-autocomplete-input) {
  display: block;
  background-color: var(--prestigio-color-bg-tertiary);
  transition-property: box-shadow;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
  font-size: 1.125rem;
  line-height: 1.2;
  padding-left: calc(1.5rem + 24px + 12px);
  padding-right: 1.5rem;
  border: 1px solid #e1e2e3;
  border-radius: 0.75rem;
  width: 100%;
  height: 4rem;
}
:deep(.p-autocomplete-input:focus) {
  border-color: #818d96;
  outline: none;
  box-shadow: none;
}
</style>
